import { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TreeItem, TreeItemButton } from '@air/component-tree';
import { Button } from '@air/primitive-button';
import { Checkbox } from '@air/primitive-checkbox';
import { type Board } from '@air/types';

import { LocationsFilterBoardTree } from '~/components/Filters/LocationsFilter/LocationsFilterBoardTree';
import { type LocationsFilterBoard } from '~/components/Filters/LocationsFilter/useLocationsFilterData';
import { useLocationsFilterStore } from '~/components/v2/store/features/filters/useLocationsFilterStore';
import { Routes } from '~/constants/routes';
import { centralizedBoardIdSelector } from '~/store/centralizedBoard/selectors';
import { setLocationsBoardsAction, toggleLocationsBoardAction } from '~/store/filters/actions';
import { selectedLocationsBoardsSelector } from '~/store/filters/selectors';
import { useSubBoardsList } from '~/swr-hooks/boards/useSubBoardsList';
import { pushWithExistingQuery } from '~/utils/PathUtils';

export type LocationsFilterBoardTreeItemProps = {
  board: Board | LocationsFilterBoard;
  hierarchyAncestorTitles?: string[];
  locked?: boolean;
  onBoardSelect?: ({ boardId }: { boardId: string }) => void;
};

const EMPTY_ANCESTOR_TITLES: string[] = [];

export const LocationsFilterBoardTreeItem = memo(
  ({
    board,
    hierarchyAncestorTitles = EMPTY_ANCESTOR_TITLES,
    locked,
    onBoardSelect,
  }: LocationsFilterBoardTreeItemProps) => {
    const prefetchedBoards = useMemo(() => ('boards' in board ? Array.from(board.boards.values()) : []), [board]);

    const dispatch = useDispatch();
    const boardId = useSelector(centralizedBoardIdSelector);
    const setTreeItemOpen = useLocationsFilterStore((store) => store.setTreeItem);
    const storeItem = useLocationsFilterStore((store) => store.getTreeItem({ id: board.id }));
    // When boards are pre-fetched (search mode), default to expanded; otherwise use store value
    const open = prefetchedBoards.length > 0 ? (storeItem?.open ?? true) : !!storeItem?.open;

    const {
      data: subBoards = [],
      isLoadingMore,
      hasMore,
      isInitialLoading,
      loadNextPage,
    } = useSubBoardsList({ boardId: board.id, canFetchData: !prefetchedBoards.length && open });

    const displayBoards = useMemo(
      () => (prefetchedBoards.length > 0 ? prefetchedBoards : subBoards),
      [prefetchedBoards, subBoards],
    );

    const descendantIds = useMemo(() => new Set(getAllDescendantIds(displayBoards)), [displayBoards]);

    const selectedBoardIds = useSelector(selectedLocationsBoardsSelector);

    const onClick = useCallback(async () => {
      if (boardId === board.id) {
        await pushWithExistingQuery({
          path: Routes.media.all,
        });
      } else {
        const isCurrentlySelected = selectedBoardIds.includes(board.id);

        // If selecting this board (not deselecting), remove any of its visible descendants from
        // selection to prevent counting both parent and children
        if (!isCurrentlySelected && descendantIds.size > 0) {
          const newSelection = selectedBoardIds.filter((id) => !descendantIds.has(id));
          dispatch(setLocationsBoardsAction({ boards: [...newSelection, board.id] }));
        } else {
          dispatch(toggleLocationsBoardAction({ board: board.id }));
        }

        onBoardSelect?.({ boardId: board.id });
      }
    }, [board.id, boardId, descendantIds, dispatch, onBoardSelect, selectedBoardIds]);

    const shouldShowSubBoards = open && !!displayBoards.length;
    const shouldShowMoreButton = !prefetchedBoards.length && open && hasMore && !isInitialLoading;

    const onOpenChange = useCallback(
      (open: boolean) => {
        setTreeItemOpen({ id: board.id, open, touched: true });
      },
      [board.id, setTreeItemOpen],
    );

    const isSelected = useMemo(
      () => board.id === boardId || selectedBoardIds.includes(board.id),
      [board.id, boardId, selectedBoardIds],
    );

    const isChecked = useMemo(() => isSelected || !!locked, [isSelected, locked]);
    const isLocked = useMemo(() => isSelected || !!locked, [isSelected, locked]);
    const isDisabled = useMemo(() => locked, [locked]);

    // Clean up: If this board is selected and we've loaded its sub-boards,
    // remove any sub-boards from the selection to prevent double-counting
    useEffect(() => {
      if (isSelected && descendantIds.size > 0 && !isInitialLoading) {
        const hasDescendantsSelected = selectedBoardIds.some((id) => descendantIds.has(id));

        if (hasDescendantsSelected) {
          const newSelection = selectedBoardIds.filter((id) => !descendantIds.has(id));
          dispatch(setLocationsBoardsAction({ boards: newSelection }));
        }
      }
    }, [isSelected, descendantIds, isInitialLoading, selectedBoardIds, dispatch]);

    const handleLoadMore = useCallback(() => {
      loadNextPage();
    }, [loadNextPage]);

    const isHierarchyBoard = 'isVisualHierarchyBoard' in board && board.isVisualHierarchyBoard;
    const hasHierarchyChildren = displayBoards.every((b) => 'isVisualHierarchyBoard' in b && b.isVisualHierarchyBoard);

    const childAncestorTitles = useMemo(
      () => [...hierarchyAncestorTitles, board.title],
      [hierarchyAncestorTitles, board.title],
    );

    // If this is a hierarchy board and all its children are also hierarchy boards pass-through
    // and continue the chain
    if (isHierarchyBoard && hasHierarchyChildren) {
      return (
        <LocationsFilterBoardTree
          boards={displayBoards}
          hierarchyAncestorTitles={childAncestorTitles}
          locked={locked}
          onBoardSelect={onBoardSelect}
        />
      );
    }

    // For final hierarchy board in a chain, render breadcrumb-style title with ellipsis
    const displayTitle =
      isHierarchyBoard && hierarchyAncestorTitles.length > 0 ? compressPath(childAncestorTitles) : board.title;

    return (
      <>
        <TreeItem
          data-id={board.id}
          onOpenChange={onOpenChange}
          open={open}
          suffix={
            <Checkbox defaultChecked={false} checked={isChecked} disabled={isDisabled} onCheckedChange={onClick} />
          }
        >
          <TreeItemButton disabled={isDisabled} onClick={onClick}>
            {isHierarchyBoard ? <span className="text-grey-9">{displayTitle}</span> : displayTitle}
          </TreeItemButton>
        </TreeItem>

        {shouldShowSubBoards && (
          <div className="ml-3">
            <LocationsFilterBoardTree boards={displayBoards} locked={isLocked} onBoardSelect={onBoardSelect} />

            {shouldShowMoreButton && (
              <Button
                color="grey"
                appearance="ghost"
                className="ml-9"
                data-testid="LOCATIONS_FILTER_SHOW_MORE"
                isLoading={isLoadingMore}
                size="small"
                onClick={handleLoadMore}
              >
                Show more
              </Button>
            )}
          </div>
        )}
      </>
    );
  },
);

LocationsFilterBoardTreeItem.displayName = 'LocationsFilterBoardTreeItem';

const getAllDescendantIds = (boards: (Board | LocationsFilterBoard)[]): string[] =>
  boards.flatMap((board) => {
    const children = 'boards' in board ? Array.from(board.boards.values()) : [];
    return [board.id, ...getAllDescendantIds(children)];
  });

const compressPath = (titles: string[]): string => {
  if (titles.length <= 2) return titles.join(' > ');
  return `${titles[0]} > … > ${titles[titles.length - 1]}`;
};
